import React, { useContext } from 'react';
import { useTranslation } from 'react-i18next';
import { useMutation } from '@apollo/client/react';
import { Tooltip } from '@progress/kendo-react-tooltip';
import ConfirmationDialog from '@components-clean/core/confirmation-dialog';
import { INamedItem } from '@components-clean/core/grid/custom-cell';
import { InfoField } from '@components-clean/core/info-field';
import { ExtendedCompleteRiskFields } from '@pages-clean/risk-assessment/risk-log/constants';
import { RiskLogContext } from '@pages-clean/risk-assessment/risk-log/interfaces/context';
import { CriticalVariableFields } from '@queries/generated/fragments.generated';
import { ASSIGN_CTQS, GET_COMPLETE_RISKS_RISK_LOG, GET_CRITICAL_VARIABLES_FOR_RISK } from '@queries/risk-assessment';

type FlattenCtq = {
    id: string;
    recordId?: number | null;
    description?: string | null;
    justification?: string | null;
    sourceSystem?: string | null;
    dataSource?: string | null;
    active?: boolean | null;
    isCritical?: boolean | null;
    cTQType?: INamedItem | null;
    cTQEDCEntry?: INamedItem | null;
    vendor?: INamedItem | null;
    createdBy?: string | null;
    updatedBy?: string | null;
    created?: string | null;
    updated?: string | null;
};

interface UnassignCtQProps {
    onClose: () => void;
    onSubmit: (result: string) => void;
    risk: ExtendedCompleteRiskFields;
    ctq: CriticalVariableFields;
    ctqs: FlattenCtq[];
}

const UnassignCtQDialog = ({ onClose, onSubmit, risk, ctq, ctqs }: UnassignCtQProps) => {
    const { t } = useTranslation('ram', { keyPrefix: 'riskLog.dialogs.unassignCtQ' });
    const { current } = useContext(RiskLogContext);
    const studyId = current?.study?.id as string | undefined;

    const [assignCtQs] = useMutation(ASSIGN_CTQS, {
        refetchQueries: [
            {
                query: GET_CRITICAL_VARIABLES_FOR_RISK,
                variables: {
                    studyId: risk.studyId,
                    riskId: risk.id,
                },
            },
            { query: GET_COMPLETE_RISKS_RISK_LOG, variables: { studyId } },
        ],
    });

    const handleSubmit = async () => {
        const filteredCtQIds = ctqs.filter((ctqf) => ctqf.id !== ctq.id).map((ctqf) => ctqf.id);

        await assignCtQs({
            variables: {
                id: risk.id,
                criticalVariableIds: filteredCtQIds,
            },
        })
            .then((response) => {
                if (response.data.assignCriticalVariables !== null) onSubmit('success');
                else onSubmit('error');
            })
            .catch(() => {
                onSubmit('error');
            });
    };

    return (
        <ConfirmationDialog
            title={t('title')}
            dataTestId="unassign-ctq-confirmation-dialog"
            onClose={() => onClose()}
            onSubmit={() => handleSubmit()}
        >
            <div>
                <InfoField
                    type="Info"
                    message={
                        <div style={{ width: '438px', margin: '0 0' }}>
                            <div className="k-font-medium">{t('riskStatement')}</div>
                            <div>{risk?.statement?.slice(0, 600)}</div>
                            <br />
                            <div className="k-d-flex k-align-items-center">
                                <div className="k-font-medium">{t('ctQDescription')}</div>
                            </div>
                            <Tooltip anchorElement="target" position="auto" parentTitle style={{ maxWidth: '600px' }}>
                                <div title={ctq?.description ?? ''}>
                                    {(() => {
                                        const desc = ctq?.description ?? '';
                                        return desc.length > 600 ? `${desc.slice(0, 600)}...` : desc;
                                    })()}
                                </div>
                            </Tooltip>
                        </div>
                    }
                />
                <div style={{ textAlign: 'center', marginTop: '30px', marginBottom: '18px' }}>{t('message')}</div>
            </div>
        </ConfirmationDialog>
    );
};

export default UnassignCtQDialog;
